import React, { useState, useCallback } from 'react';
import {
  Button,
  Input,
  Card,
  Row,
  Col,
  Form,
  message,
  Skeleton,
  Tabs,
  Select,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import AdministratorLeadsTable from '../../components/sales/AdministratorLeadsTable';
import DatabaseTab from '../../components/sales/DatabaseTab';
import AdministratorLeadDetailDrawer from '../../components/sales/AdministratorLeadDetailDrawer';
import type { AdministratorLead, LeadSource } from '../../types/administratorLeads';
import type { Contact } from '../../types/common';
import AdministratorLeadModal from '../../components/sales/AdministratorLeadModal';
import ConfirmDeleteModal from '../../components/modal/ConfirmDeleteModal';
import OpportunityModal from '../../components/sales/OpportunityModal';
import { 
  getAdministratorLeads, 
  deleteAdministratorLead, 
  addOrUpdateAdministratorOpportunity,
  updateAdministratorLeadStatus,
  toggleAdministratorLeadWhatsApp
} from '../../api/administratorLeads';
import type { QueryDocumentSnapshot } from 'firebase/firestore';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_STALE_TIME_MS } from '../../constants/api';

interface FormData {
  name: string;
  status: 'New' | 'Disqualified' | 'Lost' | 'Contacted';
  source: LeadSource;
  address: string;
  city: string;
  pincode: string;
  country: string;
  description?: string;
  notes?: string;
  contacts: {
    name: string;
    position: string;
    email: string;
    phone: string;
    type: Contact['type'];
  }[];
}


const AdministratorLeadsPage: React.FC = () => {
  const { t } = useTranslation(['common', 'sales']);
  const [form] = Form.useForm<FormData>();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLead, setEditingLead] = useState<AdministratorLead | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'New' | 'Contacted' | 'Lost' | 'Disqualified' | 'All'>('New');
  const [openOpportunityDialog, setOpenOpportunityDialog] = useState(false);
  const [opportunityForm] = Form.useForm();
  const [selectedLeadId, setSelectedLeadId] = useState<string | undefined>(undefined);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [lastDocs, setLastDocs] = useState<{ [page: number]: QueryDocumentSnapshot | undefined }>({});
  const [activeTab, setActiveTab] = useState('leads');
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [detailDrawerLead, setDetailDrawerLead] = useState<AdministratorLead | null>(null);
  const queryClient = useQueryClient();

  // Hardcoded total count for administrator leads
  const TOTAL_ADMINISTRATOR_LEADS = 4000;

  // Fetch administrator leads for the current page
  const {
    data: administratorLeadsData = { administratorLeads: [], lastDoc: undefined, total: 0 },
    isLoading,
  } = useQuery<{ administratorLeads: AdministratorLead[]; lastDoc?: QueryDocumentSnapshot; total: number }, Error>({
    queryKey: ['administratorLeads', pagination.current, pagination.pageSize, searchTerm, statusFilter],
    queryFn: async () => {
      // If searching or filtering by status, we don't need server-side pagination
      if (searchTerm || statusFilter !== 'All') {
        // For search and status filtering, we'll fetch all data and filter client-side
        const { administratorLeads: allLeads } = await getAdministratorLeads(1000); // Fetch a large number
        
        let filteredLeads = allLeads;
        
        // Apply status filter
        if (statusFilter !== 'All') {
          filteredLeads = allLeads.filter(lead => lead.status === statusFilter);
        }
        
        // Apply search filter
        if (searchTerm) {
          filteredLeads = filteredLeads.filter(lead => 
            lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            lead.contacts.some(contact => 
              contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              contact.email.toLowerCase().includes(searchTerm.toLowerCase())
            )
          );
        }
        
        return {
          administratorLeads: filteredLeads,
          lastDoc: undefined,
          total: filteredLeads.length
        };
      }
      
      // For normal pagination, use the lastDoc from the previous page
      const currentLastDoc = lastDocs[pagination.current - 1];
      
      const { administratorLeads: newLeads, lastDoc: newLastDoc } = await getAdministratorLeads(pagination.pageSize, currentLastDoc);
      
      return {
        administratorLeads: newLeads,
        lastDoc: newLastDoc,
        total: TOTAL_ADMINISTRATOR_LEADS
      };
    },
    staleTime: API_STALE_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    placeholderData: (previousData) => previousData,
  });

  // Update lastDocs when new data arrives
  React.useEffect(() => {
    if (administratorLeadsData.lastDoc) {
      setLastDocs(prev => ({
        ...prev,
        [pagination.current]: administratorLeadsData.lastDoc
      }));
    }
  }, [administratorLeadsData.lastDoc, pagination.current]);

  // Reset lastDocs when page size changes
  React.useEffect(() => {
    setLastDocs({});
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [pagination.pageSize]);

  // Reset to first page when search or status filter changes
  React.useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
    setLastDocs({});
  }, [searchTerm, statusFilter]);

  const administratorLeads = administratorLeadsData.administratorLeads;

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAdministratorLead(id),
    onSuccess: () => {
      message.success(t('success:deleteSuccess', { entity: t('sales:administratorLeads.lead') }));
      queryClient.invalidateQueries({ queryKey: ['administratorLeads'] });
    },
    onError: () => {
      message.error(t('sales:errors.deleteFailed', { entity: t('sales:administratorLeads.lead') }));
    },
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ leadId, status }: { leadId: string; status: string }) => 
      updateAdministratorLeadStatus(leadId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['administratorLeads'] });
    },
    onError: () => {
      message.error(t('sales:errors.updateFailed', { entity: t('sales:administratorLeads.lead') }));
    },
  });

  // Toggle WhatsApp mutation
  const toggleWhatsAppMutation = useMutation({
    mutationFn: ({ leadId, unavailable }: { leadId: string; unavailable: boolean }) =>
      toggleAdministratorLeadWhatsApp(leadId, unavailable),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['administratorLeads'] });
    },
    onError: () => {
      message.error(t('sales:errors.updateFailed', { entity: t('sales:administratorLeads.lead') }));
    },
  });


  // Only show leads without an opportunity
  const filteredLeads = (administratorLeads || []).filter(lead => !lead.opportunity);

  // Apply search filter
  const searchFilteredLeads = searchTerm 
    ? (filteredLeads || []).filter(lead => 
        lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.contacts?.some(contact => 
          contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          contact.phone?.includes(searchTerm)
        ) ||
        lead.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.city?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : (filteredLeads || []);

  const handlePageChange = useCallback((page: number, size?: number) => {
    // Handle page size change
    if (size && size !== pagination.pageSize) {
      setPagination(prev => ({ ...prev, pageSize: size, current: 1 }));
      setLastDocs({}); // Clear lastDocs cache
    } else {
      // Handle page navigation
      setPagination(prev => ({ ...prev, current: page }));
    }
  }, [pagination.pageSize]);

  // Handler for Ant Design Table onChange event
  const handleTableChange = useCallback((paginationInfo: any) => {
    if (paginationInfo) {
      const { current, pageSize } = paginationInfo;
      handlePageChange(current, pageSize);
    }
  }, [handlePageChange]);

  // Detail drawer handlers
  const handleOpenDetailDrawer = useCallback((lead: AdministratorLead) => {
    setDetailDrawerLead(lead);
    setIsDetailDrawerOpen(true);
  }, []);

  const handleRowClick = useCallback((lead: AdministratorLead) => {
    handleOpenDetailDrawer(lead);
  }, [handleOpenDetailDrawer]);

  const handleAdd = useCallback(() => {
    setEditingLead(null);
    form.resetFields();
    form.setFieldsValue({ status: 'New' });
    setOpenDialog(true);
  }, [form]);

  const handleEdit = useCallback((lead: AdministratorLead) => {
    setEditingLead(lead);
    form.setFieldsValue({
      name: lead.name,
      status: lead.status,
      source: lead.source,
      address: lead.address,
      city: lead.city,
      pincode: lead.pincode,
      country: lead.country,
      description: lead.description,
      notes: lead.notes,
      contacts: lead.contacts.map((contact: Contact) => ({
        name: contact.name,
        position: contact.position,
        email: contact.email,
        phone: contact.phone,
        type: contact.type,
      })),
    });
    setOpenDialog(true);
  }, [form]);

  const handleDelete = useCallback((lead: AdministratorLead) => {
    setDeleteId(lead.id);
    setOpenDeleteDialog(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
    }
  }, [deleteId, deleteMutation]);

  const handleCancelDelete = useCallback(() => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setEditingLead(null);
    form.resetFields();
  }, [form]);

  const handleAddOpportunity = useCallback((lead: AdministratorLead) => {
    setSelectedLeadId(lead.id);
    opportunityForm.resetFields();
    opportunityForm.setFieldsValue({ lead_id: lead.id });
    setOpenOpportunityDialog(true);
  }, [opportunityForm]);

  const handleUpdateStatus = useCallback((leadId: string, status: string) => {
    updateStatusMutation.mutate({ leadId, status });
  }, [updateStatusMutation]);

  const handleToggleWhatsApp = useCallback((leadId: string, unavailable: boolean) => {
    toggleWhatsAppMutation.mutate({ leadId, unavailable });
  }, [toggleWhatsAppMutation]);

  const handleCloseDetailDrawer = useCallback(() => {
    setIsDetailDrawerOpen(false);
    setDetailDrawerLead(null);
  }, []);

  const handleEditFromDrawer = useCallback((lead: AdministratorLead) => {
    setEditingLead(lead);
    form.setFieldsValue({
      name: lead.name,
      status: lead.status,
      source: lead.source,
      address: lead.address,
      city: lead.city,
      pincode: lead.pincode,
      country: lead.country,
      description: lead.description,
      notes: lead.notes,
      contacts: lead.contacts?.map(contact => ({
        name: contact.name,
        position: contact.position,
        email: contact.email,
        phone: contact.phone,
        type: contact.type,
      })) || [],
    });
    setOpenDialog(true);
    handleCloseDetailDrawer();
  }, [form, handleCloseDetailDrawer]);


  // Handler for search input
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page when searching
  }, []);

  // Handler for status filter
  const handleStatusFilterChange = useCallback((value: 'New' | 'Contacted' | 'Lost' | 'Disqualified' | 'All') => {
    setStatusFilter(value);
    setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page when filtering
  }, []);

  // Calculate pagination for search results (client-side) or server-side
  const getPaginatedLeads = () => {
    if (searchTerm) {
      // Client-side pagination for search results
      const startIndex = (pagination.current - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      return searchFilteredLeads.slice(startIndex, endIndex);
    }
    // Server-side pagination for all results - use the data directly from the query
    return administratorLeads;
  };

  const paginatedLeads = getPaginatedLeads();

  // Calculate total count safely
  const totalCount = searchTerm 
    ? (searchFilteredLeads?.length || 0)
    : (administratorLeadsData?.total || 0);

  // Pagination config for AntD Table
  const paginationConfig = {
    current: Math.max(1, pagination.current || 1), // Ensure current is always positive
    pageSize: Math.max(1, pagination.pageSize || 10), // Ensure pageSize is always positive
    total: Math.max(0, totalCount), // Ensure total is never negative
    showTotal: (total: number, range: [number, number]) => 
      t('common:pagination.showing', { 
        start: range[0] || 0, 
        end: range[1] || 0, 
        total: total || 0
      }),
    showSizeChanger: true,
    showQuickJumper: !searchTerm, // Disable quick jumper when searching (client-side pagination)
    pageSizeOptions: ['10', '25', '50', '100'],
    onChange: handlePageChange,
  };



  // Optimistic update for add/edit (handled in mutation, not shown here)
  const handleLeadModalSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['administratorLeads'] });
    handleCloseDialog();
  }, [queryClient, handleCloseDialog]);

  // Add opportunity mutation
  const addOpportunityMutation = useMutation({
    mutationFn: ({ leadId, opportunity }: { leadId: string; opportunity: any }) => addOrUpdateAdministratorOpportunity(leadId, opportunity),
    onSuccess: () => {
      message.success(t('success:createSuccess', { entity: t('opportunities.opportunity') }));
      queryClient.invalidateQueries({ queryKey: ['administratorLeads'] });
      setOpenOpportunityDialog(false);
    },
    onError: () => {
      message.error(t('errors.saveFailed', { entity: t('opportunities.opportunity') }));
    },
  });

  // Save handler for OpportunityModal
  const handleOpportunitySave = async () => {
    try {
      const values = await opportunityForm.validateFields();
      const opportunity = {
        stage: values.stage,
        value: values.price,
        expected_close_date: values.follow_up_date instanceof Date ? values.follow_up_date : values.follow_up_date.toDate(),
        units: values.units,
      };
      addOpportunityMutation.mutate({ leadId: selectedLeadId!, opportunity });
    } catch (error) {
      message.error(t('errors.saveFailed', { entity: t('opportunities.opportunity') }));
    }
  };

  // Handler for discovery tab lead view
  const handleViewLead = useCallback((lead: any) => {
    handleOpenDetailDrawer(lead);
  }, [handleOpenDetailDrawer]);

  // Tab items configuration
  const tabItems = [
    {
      key: 'leads',
      label: t('sales:administratorLeads.title', 'Administrator Leads'),
      children: (
        <div style={{ width: '100%' }}>
          {isLoading && administratorLeads.length === 0 ? (
            <Card style={{ marginTop: 24 }}><Skeleton active /></Card>
          ) : (
            <>
            <Row 
              justify="space-between" 
              align="middle" 
              style={{ marginBottom: 24, marginTop: 24}}
              gutter={[{ xs: 8, sm: 16 }, { xs: 8, sm: 16 }]}
            >
              <Col xs={24} sm={12} md={14}>
                <Input
                  placeholder={t('sales:administratorLeads.searchPlaceholder')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Col>
              <Col xs={12} sm={6} md={4}>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  style={{ width: '100%' }}
                  options={[
                    { value: 'New', label: t('sales:administratorLeads.status.new', 'New') },
                    { value: 'Contacted', label: t('sales:administratorLeads.status.contacted', 'Contacted') },
                    { value: 'Lost', label: t('sales:administratorLeads.status.lost', 'Lost') },
                    { value: 'Disqualified', label: t('sales:administratorLeads.status.disqualified', 'Disqualified') },
                    { value: 'All', label: t('sales:administratorLeads.status.all', 'All') },
                  ]}
                />
              </Col>
              <Col xs={12} sm={6} md={6} style={{ textAlign: 'right' }}>
                <Button
                  type="primary"
                  onClick={handleAdd}
                  icon={<PlusOutlined />}
                >
                  {t('sales:administratorLeads.add')}
                </Button>
              </Col>
            </Row>

            <Card variant="borderless" style={{ boxShadow: '0 0 10px rgba(0,0,0,0.1)' }}>
              <AdministratorLeadsTable
                key={`leads-${paginatedLeads.length}-${pagination.current}-${pagination.pageSize}`}
                leads={paginatedLeads}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleRowClick}
                onAddOpportunity={handleAddOpportunity}
                onUpdateStatus={handleUpdateStatus}
                onToggleWhatsApp={handleToggleWhatsApp}
                onOpenDetailDrawer={handleOpenDetailDrawer}
                loading={isLoading}
                pagination={isLoading ? false : paginationConfig}
                onTableChange={handleTableChange}
              />
            </Card>
            </>
          )}
        </div>
      ),
    },
    {
      key: 'database',
      label: t('sales:database.title', 'Database'),
      children: <DatabaseTab onViewLead={handleViewLead} />,
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        items={tabItems}
        style={{ marginTop: 24 }}
      />

      <AdministratorLeadModal
        open={openDialog}
        lead={editingLead}
        onSuccess={handleLeadModalSuccess}
        onCancel={handleCloseDialog}
      />

      <ConfirmDeleteModal
        open={openDeleteDialog}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title={t('common:confirmDelete')}
        description={t('sales:administratorLeads.deleteConfirm')}
        okText={t('common:delete')}
        cancelText={t('common:cancel')}
        okButtonProps={{ danger: true }}
      />

      <OpportunityModal
        open={openOpportunityDialog}
        onOk={handleOpportunitySave}
        onCancel={() => setOpenOpportunityDialog(false)}
        form={opportunityForm}
        leads={filteredLeads}
        editingOpportunity={null}
        defaultLeadId={selectedLeadId}
      />

      <AdministratorLeadDetailDrawer
        lead={detailDrawerLead}
        open={isDetailDrawerOpen}
        onClose={handleCloseDetailDrawer}
        onUpdateStatus={handleUpdateStatus}
        onToggleWhatsApp={handleToggleWhatsApp}
        onEdit={handleEditFromDrawer}
      />
    </div>
  );
};

export default AdministratorLeadsPage;
